import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../../../environment";

interface EntityField {
  label: string;
  value: string | number | null;
}

interface ApprovalDetail {
  can_take_action: boolean;
  message: string;
  title: string;
  entity: {
    entity_name: string;
    entity_label: string;
    entity_id: number;
    fields: EntityField[];
  };
  approval: {
    policy_name: string;
    instance_id: number;
    instance_status: string;
    current_stage_order: number;
    stage_name: string;
    stage_status: string;
    stage_condition: string;
    approver_status: string;
    recipient_email: string;
    token_expiry_datetime: string;
  };
}

type FetchErrorKind = "expired" | "already_approved" | "stage_inactive" | "server_error" | "generic";
type FinalStateKind = "approved" | "rejected" | "expired" | "already_approved" | "stage_inactive";

const classifyError = (msg: string | undefined): FetchErrorKind => {
  if (!msg) return "server_error";
  if (msg.includes("Invalid") || msg.includes("expired") || msg.includes("already used")) return "expired";
  if (msg.includes("already been approved")) return "already_approved";
  if (msg.includes("no longer active")) return "stage_inactive";
  if (msg.includes("Unable to process")) return "server_error";
  return "generic";
};

const FETCH_ERROR_CONFIG: Record<FetchErrorKind, {
  icon: string; color: string; bg: string; border: string; title: string; body: string;
}> = {
  expired: {
    icon: "ti ti-link-off",
    color: "#d97706",
    bg: "#fffbeb",
    border: "#fde68a",
    title: "Link Expired or Invalid",
    body: "This approval link has expired, is invalid, or has already been used. Please contact the sender to request a new link.",
  },
  already_approved: {
    icon: "ti ti-circle-check",
    color: "#5CB85C",
    bg: "#f0fdf4",
    border: "#bbf7d0",
    title: "Already Approved",
    body: "This request has already been approved. No further action is required.",
  },
  stage_inactive: {
    icon: "ti ti-clock-off",
    color: "#6b7280",
    bg: "#f9fafb",
    border: "#e5e7eb",
    title: "Stage No Longer Active",
    body: "This approval stage is no longer active. The workflow may have progressed or been cancelled.",
  },
  server_error: {
    icon: "ti ti-server-off",
    color: "#FC0027",
    bg: "#fef2f2",
    border: "#fecaca",
    title: "Something Went Wrong",
    body: "We couldn't load the approval details. Please try refreshing the page or contact support.",
  },
  generic: {
    icon: "ti ti-alert-triangle",
    color: "#d97706",
    bg: "#fffbeb",
    border: "#fde68a",
    title: "Unable to Load",
    body: "Failed to load approval details. Please check your link or try again.",
  },
};

const FINAL_STATE_CONFIG: Record<FinalStateKind, {
  icon: string; iconColor: string; iconBg: string; title: string; body: string;
}> = {
  approved: {
    icon: "ti ti-check",
    iconColor: "#5CB85C",
    iconBg: "#f0fdf4",
    title: "Approved!",
    body: "Your approval has been submitted successfully. Thank you for your response.",
  },
  rejected: {
    icon: "ti ti-x",
    iconColor: "#FC0027",
    iconBg: "#fef2f2",
    title: "Rejected",
    body: "You have rejected this request. Your response has been recorded.",
  },
  expired: {
    icon: "ti ti-link-off",
    iconColor: "#d97706",
    iconBg: "#fffbeb",
    title: "Link Expired",
    body: "This approval link has expired or was already used. Please contact the sender to request a new link.",
  },
  already_approved: {
    icon: "ti ti-circle-check",
    iconColor: "#5CB85C",
    iconBg: "#f0fdf4",
    title: "Already Approved",
    body: "This request has already been approved. No further action is needed.",
  },
  stage_inactive: {
    icon: "ti ti-clock-off",
    iconColor: "#6b7280",
    iconBg: "#f9fafb",
    title: "Stage No Longer Active",
    body: "This approval stage is no longer active. The workflow may have progressed or been cancelled.",
  },
};

const formatFieldValue = (value: string | number | null): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "number") return value.toLocaleString();
  const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
  if (dateRegex.test(String(value))) {
    const d = new Date(String(value));
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return String(value);
};

const statusConfig: Record<string, { bg: string; label: string }> = {
  pending: { bg: "#FFA201", label: "Pending" },
  in_progress: { bg: "#1ECBE2", label: "In Progress" },
  approved: { bg: "#5CB85C", label: "Approved" },
  rejected: { bg: "#FC0027", label: "Rejected" },
  completed: { bg: "#5CB85C", label: "Completed" },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const key = status.toLowerCase();
  const config = statusConfig[key] || { bg: "#6c757d", label: status };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 600,
        color: "#fff",
        background: config.bg,
        textTransform: "capitalize",
        letterSpacing: 0.3,
      }}
    >
      {config.label || status.replace(/_/g, " ")}
    </span>
  );
};

const ExternalApproval: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [data, setData] = useState<ApprovalDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchErrorKind, setFetchErrorKind] = useState<FetchErrorKind | null>(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [finalState, setFinalState] = useState<FinalStateKind | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setFetchErrorKind("expired");
      setLoading(false);
      return;
    }
    axios
      .get(`${API_BASE_URL}/approval-action/detail/?token=${token}`)
      .then((res) => {
        setData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        const msg: string = err?.response?.data?.error || "";
        setFetchErrorKind(classifyError(msg));
        setLoading(false);
      });
  }, [token]);

  const handleAction = async (actionType: "APPROVED" | "REJECTED") => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await axios.post(`${API_BASE_URL}/approval-action/submit/`, {
        from_crm: false,
        token,
        action: actionType,
        comment: comment.trim() || undefined,
      });
      setFinalState(actionType === "APPROVED" ? "approved" : "rejected");
    } catch (err: any) {
      const msg: string = err?.response?.data?.error || "";
      const kind = classifyError(msg);
      if (kind === "expired" || kind === "already_approved" || kind === "stage_inactive") {
        setFinalState(kind);
      } else {
        setSubmitError(msg || "Failed to submit your response. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f4f5f7", fontFamily: "'Noto Sans', sans-serif", display: "flex", flexDirection: "column" }}>
      <header
        style={{
          background: "#fff",
          borderBottom: "1px solid #e5e7eb",
          padding: "0 32px",
          height: 64,
          display: "flex",
          alignItems: "center",
          position: "sticky",
          top: 0,
          zIndex: 100,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        }}
      >
        <div style={{ height: 70, overflow: "hidden", display: "flex", alignItems: "center" }}>
          <img
            src="/assets/img/Logo.png"
            alt="Cloudee CRM"
            style={{ height: 70, width: "auto", display: "block" }}
          />
        </div>
      </header>

      <main style={{ maxWidth: 860, margin: "0 auto", padding: "40px 20px 60px", flex: 1, width: "100%" }}>
        {loading && (
          <div style={{ textAlign: "center", paddingTop: 80 }}>
            <div
              className="spinner-border"
              role="status"
              style={{ color: "#824eeb", width: 40, height: 40 }}
            />
            <p style={{ color: "#6b7280", marginTop: 16, fontSize: 15 }}>
              Loading approval details...
            </p>
          </div>
        )}

        {!loading && fetchErrorKind && (() => {
          const cfg = FETCH_ERROR_CONFIG[fetchErrorKind];
          return (
            <div
              style={{
                background: cfg.bg,
                border: `1px solid ${cfg.border}`,
                borderRadius: 12,
                padding: "48px 32px",
                textAlign: "center",
                boxShadow: "0 1px 8px rgba(0,0,0,0.07)",
              }}
            >
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  background: "#fff",
                  border: `2px solid ${cfg.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                }}
              >
                <i className={cfg.icon} style={{ fontSize: 32, color: cfg.color }} />
              </div>
              <h4 style={{ margin: "0 0 10px", color: "#1f2937", fontWeight: 700, fontSize: 20 }}>
                {cfg.title}
              </h4>
              <p style={{ color: "#6b7280", fontSize: 14, maxWidth: 420, margin: "0 auto", lineHeight: 1.6 }}>
                {cfg.body}
              </p>
            </div>
          );
        })()}

        {!loading && data && !finalState && (
          <>
            {!data.can_take_action && (
              <div
                style={{
                  background: "#fffbeb",
                  border: "1px solid #fcd34d",
                  borderRadius: 10,
                  padding: "14px 20px",
                  marginBottom: 24,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <i className="ti ti-info-circle" style={{ color: "#d97706", fontSize: 20 }} />
                <span style={{ color: "#92400e", fontSize: 14, fontWeight: 500 }}>
                  {data.message || "Action cannot be taken on this approval request."}
                </span>
              </div>
            )}

            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                marginBottom: 20,
                boxShadow: "0 1px 8px rgba(0,0,0,0.07)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "18px 24px",
                  borderBottom: "1px solid #f0f0f0",
                  background: "linear-gradient(135deg, #824eeb08, #5c8fff08)",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: "#824eeb15",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <i className="ti ti-file-description" style={{ color: "#824eeb", fontSize: 18 }} />
                </div>
                <div>
                  <h5 style={{ margin: 0, fontWeight: 700, color: "#1f2937", fontSize: 16 }}>
                    {data.title}
                  </h5>
                  <span style={{ color: "#9ca3af", fontSize: 12, textTransform: "capitalize" }}>
                    {data.entity.entity_name}
                  </span>
                </div>
              </div>
              <div style={{ padding: "20px 24px" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                    gap: "0",
                  }}
                >
                  {data.entity.fields.map((field, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: "12px 16px",
                        borderBottom: "1px solid #f3f4f6",
                        borderRight: idx % 2 === 0 ? "1px solid #f3f4f6" : "none",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#9ca3af",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          marginBottom: 4,
                        }}
                      >
                        {field.label}
                      </div>
                      <div style={{ fontSize: 14, color: "#1f2937", fontWeight: 500 }}>
                        {formatFieldValue(field.value)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                marginBottom: 20,
                boxShadow: "0 1px 8px rgba(0,0,0,0.07)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "18px 24px",
                  borderBottom: "1px solid #f0f0f0",
                  background: "linear-gradient(135deg, #1ECBE208, #824eeb08)",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: "#1ECBE215",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <i className="ti ti-shield-check" style={{ color: "#1ECBE2", fontSize: 18 }} />
                </div>
                <div>
                  <h5 style={{ margin: 0, fontWeight: 700, color: "#1f2937", fontSize: 16 }}>
                    Approval Policy
                  </h5>
                  <span style={{ color: "#9ca3af", fontSize: 12 }}>
                    {data.approval.policy_name}
                  </span>
                </div>
              </div>
              <div style={{ padding: "20px 24px" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                    gap: 16,
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      background: "#f9fafb",
                      borderRadius: 8,
                      padding: "14px 16px",
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#9ca3af",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        marginBottom: 6,
                      }}
                    >
                      Stage
                    </div>
                    <div style={{ fontWeight: 600, color: "#1f2937", fontSize: 14 }}>
                      {data.approval.stage_name}
                    </div>
                    <div style={{ color: "#6b7280", fontSize: 12, marginTop: 2 }}>
                      Stage {data.approval.current_stage_order}
                    </div>
                  </div>

                  <div
                    style={{
                      background: "#f9fafb",
                      borderRadius: 8,
                      padding: "14px 16px",
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#9ca3af",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        marginBottom: 6,
                      }}
                    >
                      Stage Status
                    </div>
                    <StatusBadge status={data.approval.stage_status} />
                    <div style={{ color: "#6b7280", fontSize: 12, marginTop: 4 }}>
                      {data.approval.stage_condition}
                    </div>
                  </div>

                  <div
                    style={{
                      background: "#f9fafb",
                      borderRadius: 8,
                      padding: "14px 16px",
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#9ca3af",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        marginBottom: 6,
                      }}
                    >
                      Your Status
                    </div>
                    <StatusBadge status={data.approval.approver_status} />
                    <div style={{ color: "#6b7280", fontSize: 12, marginTop: 4 }}>
                      {data.approval.recipient_email}
                    </div>
                  </div>

                  <div
                    style={{
                      background: "#f9fafb",
                      borderRadius: 8,
                      padding: "14px 16px",
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#9ca3af",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        marginBottom: 6,
                      }}
                    >
                      Link Expires
                    </div>
                    <div style={{ fontWeight: 600, color: "#1f2937", fontSize: 14 }}>
                      {formatFieldValue(data.approval.token_expiry_datetime)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {data.can_take_action && (
              <div
                style={{
                  background: "#fff",
                  borderRadius: 12,
                  boxShadow: "0 1px 8px rgba(0,0,0,0.07)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "18px 24px",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <h5 style={{ margin: 0, fontWeight: 700, color: "#1f2937", fontSize: 16 }}>
                    Your Decision
                  </h5>
                  <p style={{ color: "#9ca3af", fontSize: 13, margin: "4px 0 0" }}>
                    Review the details above before taking action. A comment is optional.
                  </p>
                </div>
                <div style={{ padding: "24px" }}>
                  <div style={{ marginBottom: 20 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 8,
                      }}
                    >
                      Comment{" "}
                      <span style={{ fontWeight: 400, color: "#9ca3af" }}>(optional)</span>
                    </label>
                    <textarea
                      rows={3}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Add a comment for context..."
                      style={{
                        width: "100%",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: "10px 14px",
                        fontSize: 14,
                        color: "#1f2937",
                        resize: "vertical",
                        outline: "none",
                        transition: "border-color 0.2s",
                        fontFamily: "inherit",
                        boxSizing: "border-box",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#824eeb")}
                      onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                    />
                  </div>

                  {submitError && (
                    <div
                      style={{
                        background: "#fef2f2",
                        border: "1px solid #fecaca",
                        borderRadius: 8,
                        padding: "12px 16px",
                        marginBottom: 16,
                        color: "#b91c1c",
                        fontSize: 13,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                      }}
                    >
                      <i className="ti ti-alert-circle" style={{ fontSize: 17, marginTop: 1, flexShrink: 0 }} />
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: 2 }}>Submission Failed</div>
                        <div style={{ color: "#dc2626" }}>{submitError}</div>
                      </div>
                    </div>
                  )}

                  <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                    <button
                      onClick={() => handleAction("REJECTED")}
                      disabled={submitting}
                      style={{
                        padding: "10px 24px",
                        borderRadius: 8,
                        border: "1.5px solid #FC0027",
                        background: "#fff",
                        color: "#FC0027",
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: submitting ? "not-allowed" : "pointer",
                        opacity: submitting ? 0.6 : 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        transition: "background 0.15s",
                        fontFamily: "inherit",
                      }}
                      onMouseEnter={(e) => {
                        if (!submitting) {
                          (e.currentTarget as HTMLButtonElement).style.background = "#fef2f2";
                        }
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background = "#fff";
                      }}
                    >
                      <i className="ti ti-x" style={{ fontSize: 16 }} />
                      Reject
                    </button>
                    <button
                      onClick={() => handleAction("APPROVED")}
                      disabled={submitting}
                      style={{
                        padding: "10px 24px",
                        borderRadius: 8,
                        border: "none",
                        background: "#824eeb",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: submitting ? "not-allowed" : "pointer",
                        opacity: submitting ? 0.6 : 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        transition: "background 0.15s",
                        fontFamily: "inherit",
                      }}
                      onMouseEnter={(e) => {
                        if (!submitting)
                          (e.currentTarget as HTMLButtonElement).style.background = "#6d3fd4";
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background = "#824eeb";
                      }}
                    >
                      {submitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                            style={{ width: 14, height: 14 }}
                          />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-check" style={{ fontSize: 16 }} />
                          Approve
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {!loading && finalState && (() => {
          const cfg = FINAL_STATE_CONFIG[finalState];
          const showComment = comment && (finalState === "approved" || finalState === "rejected");
          return (
            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                padding: "56px 32px",
                textAlign: "center",
                boxShadow: "0 1px 8px rgba(0,0,0,0.07)",
              }}
            >
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  background: cfg.iconBg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                }}
              >
                <i
                  className={cfg.icon}
                  style={{ fontSize: 32, color: cfg.iconColor }}
                />
              </div>
              <h4 style={{ fontWeight: 700, color: "#1f2937", marginBottom: 8, fontSize: 22 }}>
                {cfg.title}
              </h4>
              <p style={{ color: "#6b7280", fontSize: 15, maxWidth: 400, margin: "0 auto" }}>
                {cfg.body}
              </p>
              {showComment && (
                <div
                  style={{
                    marginTop: 20,
                    background: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    padding: "12px 16px",
                    maxWidth: 400,
                    margin: "20px auto 0",
                    textAlign: "left",
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#9ca3af",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      marginBottom: 4,
                    }}
                  >
                    Your Comment
                  </div>
                  <div style={{ fontSize: 14, color: "#374151" }}>{comment}</div>
                </div>
              )}
            </div>
          );
        })()}
      </main>

      <footer
        style={{
          textAlign: "center",
          padding: "20px",
          color: "#9ca3af",
          fontSize: 12,
          borderTop: "1px solid #e5e7eb",
          background: "#fff",
        }}
      >
        © {new Date().getFullYear()} • Cloudee CRM
      </footer>
    </div>
  );
};

export default ExternalApproval;
